import React, { useEffect, useMemo, useState } from 'react';
import { orchestratorApi } from '../api/orchestrator';
import './GoogleCalendarAuth.css';

type TaskStatus = 'pending' | 'assigned' | 'in_progress' | 'resolved' | 'cancelled' | 'escalated';

interface ManagementTask {
  id: string;
  chat_id?: string;
  phone_number?: string;
  reason: string;
  status: TaskStatus;
  created_at: string;
  updated_at: string;
  context?: Record<string, any>;
}

interface ManagementStats {
  by_status: Record<string, number>;
  total: number;
}

interface ManagementMessagesDashboardProps {
  selectedEntityId?: string;
}

const STATUS_COLORS: Record<TaskStatus, string> = {
  pending: '#f59e0b',
  assigned: '#3b82f6',
  in_progress: '#8b5cf6',
  resolved: '#10b981',
  cancelled: '#6b7280',
  escalated: '#ef4444',
};

export const ManagementMessagesDashboard: React.FC<ManagementMessagesDashboardProps> = ({ selectedEntityId }) => {
  const [tasks, setTasks] = useState<ManagementTask[]>([]);
  const [stats, setStats] = useState<ManagementStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedTask, setSelectedTask] = useState<ManagementTask | null>(null);

  const queryString = useMemo(() => {
    const params = new URLSearchParams({ category: 'management_message', limit: '100', offset: '0' });
    if (selectedEntityId) {
      params.set('entity_id', selectedEntityId);
    }
    return params.toString();
  }, [selectedEntityId]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const headers = await orchestratorApi.getAuthHeaders();
        const [tasksResponse, statsResponse] = await Promise.all([
          fetch(`/api/handoff/tasks?${queryString}`, { headers }),
          fetch(`/api/handoff/stats?${queryString}`, { headers }),
        ]);

        const tasksData = await tasksResponse.json();
        const statsData = await statsResponse.json();

        if (tasksData.ok) {
          setTasks(tasksData.tasks || []);
          setSelectedTask((current) => {
            if (!current) {
              return tasksData.tasks?.[0] || null;
            }
            return tasksData.tasks?.find((task: ManagementTask) => task.id === current.id) || tasksData.tasks?.[0] || null;
          });
        } else {
          setTasks([]);
          setSelectedTask(null);
        }

        if (statsData.ok) {
          setStats(statsData.stats || null);
        } else {
          setStats(null);
        }
      } catch (error) {
        console.error('Failed to load management messages:', error);
        setTasks([]);
        setStats(null);
        setSelectedTask(null);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [queryString]);

  const formatDate = (value?: string) => {
    if (!value) return 'Unknown';
    return new Date(value).toLocaleString('en-SG', {
      timeZone: 'Asia/Singapore',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="google-calendar-auth">
      <div className="gca-header">
        <h2>📬 Messages For Management</h2>
        <p className="gca-description">
          Non-client commercial enquiries such as partnerships, referrals, broker outreach, or vendor proposals are stored here for follow-up.
        </p>
      </div>

      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '15px', marginBottom: '20px' }}>
          <div className="gca-card" style={{ padding: '15px', textAlign: 'center' }}>
            <div style={{ fontSize: '30px', fontWeight: 'bold', color: '#3b82f6' }}>{stats.total}</div>
            <div style={{ fontSize: '14px', color: '#6b7280' }}>Tracked Messages</div>
          </div>
          <div className="gca-card" style={{ padding: '15px', textAlign: 'center' }}>
            <div style={{ fontSize: '30px', fontWeight: 'bold', color: '#f59e0b' }}>{stats.by_status.pending || 0}</div>
            <div style={{ fontSize: '14px', color: '#6b7280' }}>Pending</div>
          </div>
          <div className="gca-card" style={{ padding: '15px', textAlign: 'center' }}>
            <div style={{ fontSize: '30px', fontWeight: 'bold', color: '#10b981' }}>{stats.by_status.resolved || 0}</div>
            <div style={{ fontSize: '14px', color: '#6b7280' }}>Resolved</div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="gca-card" style={{ padding: '2rem', textAlign: 'center' }}>Loading management messages...</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(320px, 420px) 1fr', gap: '20px', alignItems: 'start' }}>
          <div className="gca-card">
            <h3 style={{ marginBottom: '1rem' }}>Tracked Messages</h3>
            {tasks.length === 0 ? (
              <p className="gca-empty">No management messages found for this entity yet.</p>
            ) : (
              <div style={{ display: 'grid', gap: '12px' }}>
                {tasks.map((task) => (
                  <button
                    key={task.id}
                    onClick={() => setSelectedTask(task)}
                    style={{
                      textAlign: 'left',
                      border: selectedTask?.id === task.id ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                      background: selectedTask?.id === task.id ? '#eff6ff' : '#fff',
                      borderRadius: '10px',
                      padding: '0.9rem',
                      cursor: 'pointer'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', marginBottom: '8px' }}>
                      <strong>{task.context?.contact_name || task.phone_number || 'Unknown contact'}</strong>
                      <span style={{ color: STATUS_COLORS[task.status], fontWeight: 700, fontSize: '0.85rem' }}>
                        {task.status.replace('_', ' ')}
                      </span>
                    </div>
                    <div style={{ color: '#374151', fontSize: '0.92rem', marginBottom: '6px' }}>
                      {task.context?.topic || task.reason}
                    </div>
                    <div style={{ color: '#6b7280', fontSize: '0.8rem' }}>
                      {task.phone_number || task.context?.phone_number || 'No phone'} · {formatDate(task.created_at)}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="gca-card">
            <h3 style={{ marginBottom: '1rem' }}>Conversation Detail</h3>
            {selectedTask ? (
              <div style={{ display: 'grid', gap: '14px' }}>
                <div>
                  <strong>Name:</strong> {selectedTask.context?.contact_name || 'Unknown'}
                </div>
                <div>
                  <strong>Phone:</strong> {selectedTask.phone_number || selectedTask.context?.phone_number || 'Unknown'}
                </div>
                <div>
                  <strong>Topic:</strong> {selectedTask.context?.topic || selectedTask.reason}
                </div>
                <div>
                  <strong>Status:</strong>{' '}
                  <span style={{ color: STATUS_COLORS[selectedTask.status], fontWeight: 700 }}>
                    {selectedTask.status.replace('_', ' ')}
                  </span>
                </div>
                <div>
                  <strong>Detected:</strong> {selectedTask.context?.detection_reason || 'Rule-based non-client management enquiry'}
                </div>
                <div>
                  <strong>Created:</strong> {formatDate(selectedTask.created_at)}
                </div>
                <div>
                  <strong>Conversation:</strong>
                  <pre style={{
                    marginTop: '8px',
                    whiteSpace: 'pre-wrap',
                    background: '#f8fafc',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    padding: '1rem',
                    maxHeight: '460px',
                    overflow: 'auto',
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                    fontSize: '0.9rem'
                  }}>
                    {selectedTask.context?.conversation_history || selectedTask.context?.latest_inbound_message || 'No conversation captured'}
                  </pre>
                </div>
              </div>
            ) : (
              <p className="gca-empty">Select a message to view the full conversation.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ManagementMessagesDashboard;
